import { useMutation, useQuery } from "@tanstack/react-query"
import { Card, Divider, message } from "antd"
import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import ArticleSearchForm from "./components/ArticleSearchForm"
import ArticleTable from "./components/ArticleTable"
import ArticleTableActions from "./components/ArticleTableActions"
import { articleService, type Article, type ArticleSearchParams } from "@/services/article/articleApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const ArticlePage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useTableParams<ArticleSearchParams>({
    ...PAGE_CONFIG,
    search: "",
    type: undefined,
    status: undefined,
  })
  const [total, setTotal] = useState<number>(0)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["articles_list", searchParams],
    queryFn: () => {
      return articleService.list({
        page: searchParams.page,
        per_page: searchParams.per_page,
        search: searchParams.search || undefined,
        type: searchParams.type || undefined,
        status: searchParams.status || undefined,
      })
    },
  })

  useEffect(() => {
    if (result?.paginate?.total !== undefined) {
      setTotal(result.paginate.total)
    }
  }, [result?.paginate])

  const deleteArticleMutation = useMutation({
    mutationFn: (id: number) => articleService.delete(id),
    onSuccess: (res) => {
      if (res.code === 200) {
        message.success("Xóa bài viết thành công")
        refetch()
      } else {
        message.error(res.message || "Xóa thất bại")
      }
    },
    onError: (err: any) => {
      message.error(err.message || "Có lỗi xảy ra")
    },
  })

  const handleSearch = (values: Omit<ArticleSearchParams, "page" | "per_page">) => {
    setSearchParams({
      ...values,
      page: 1,
      per_page: searchParams.per_page,
    })
  }

  const handleAdd = () => {
    navigate("/article/create")
  }

  const handleEdit = (record: Article) => {
    navigate(`/article/edit/${record.id}`)
  }

  const handleDelete = (id: number) => {
    deleteArticleMutation.mutate(id)
  }

  const tableLoading = isFetching || deleteArticleMutation.isPending

  return (
    <div className="flex h-full flex-col gap-2">
      <ArticleSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          type: searchParams.type,
          status: searchParams.status,
        }}
      />

      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow" }}
        title={
          <div className="flex items-center">
            <h2>Quản lý thông báo / bài viết</h2>
            <Divider orientation="vertical" />
            <ArticleTableActions onAdd={handleAdd} onRefresh={refetch} loading={tableLoading} />
          </div>
        }
      >
        <ArticleTable
          data={result?.data || []}
          loading={tableLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page: page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>
    </div>
  )
}

export default ArticlePage
